import React, { useMemo } from 'react';
import { Card, Row, Col, Statistic, Typography } from 'antd';
import { 
  DollarCircleOutlined, ShoppingCartOutlined, 
  TagsOutlined, CreditCardOutlined,
  ArrowUpOutlined, ArrowDownOutlined 
} from '@ant-design/icons';

const { Text } = Typography;

const SummaryCards = ({ data, previousData }) => {
  // Toplamları hesapla
  const { summaryData, previousSummaryData } = useMemo(() => {
    const calculateSummary = (dataSet) => {
      if (!dataSet || dataSet.length === 0) {
        return {
          totalRevenue: 0,
          totalOrders: 0,
          totalProductSales: 0,
          totalCost: 0
        };
      }

      return dataSet.reduce((acc, item) => {
        const itemData = item.data || {};
        
        acc.totalRevenue += Number(itemData.revenue) || 0;
        acc.totalOrders += Number(itemData.orders) || 0;
        acc.totalProductSales += Number(itemData.productSales) || 0;
        
        // Toplam maliyet hesapla
        const metaCost = Number(itemData.metaCost) || 0;
        const googleCost = Number(itemData.googleCost) || 0;
        const metaBoostCost = Number(itemData.metaBoostCost) || 0;
        acc.totalCost += (metaCost + googleCost + metaBoostCost);
        
        return acc;
      }, {
        totalRevenue: 0,
        totalOrders: 0,
        totalProductSales: 0,
        totalCost: 0
      });
    };

    return {
      summaryData: calculateSummary(data),
      previousSummaryData: calculateSummary(previousData)
    };
  }, [data, previousData]);

  // Değişim yüzdesini hesapla
  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    return ((current - previous) / previous) * 100;
  };

  // Formatla
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const cards = [
    {
      title: 'Toplam Gelir',
      value: summaryData.totalRevenue,
      previousValue: previousSummaryData.totalRevenue,
      icon: <DollarCircleOutlined style={{ color: '#52c41a' }} />,
      formatter: formatCurrency,
      color: '#52c41a',
      key: 'totalRevenue'
    },
    {
      title: 'Toplam Sipariş',
      value: summaryData.totalOrders,
      previousValue: previousSummaryData.totalOrders,
      icon: <ShoppingCartOutlined style={{ color: '#1890ff' }} />,
      formatter: (value) => value.toLocaleString(),
      color: '#1890ff',
      key: 'totalOrders'
    },
    {
      title: 'Toplam Ürün Satışı',
      value: summaryData.totalProductSales,
      previousValue: previousSummaryData.totalProductSales,
      icon: <TagsOutlined style={{ color: '#722ed1' }} />,
      formatter: (value) => value.toLocaleString(),
      color: '#722ed1',
      key: 'totalProductSales'
    },
    {
      title: 'Toplam Maliyet',
      value: summaryData.totalCost,
      previousValue: previousSummaryData.totalCost,
      icon: <CreditCardOutlined style={{ color: '#fa8c16' }} />,
      formatter: formatCurrency,
      color: '#fa8c16',
      key: 'totalCost'
    }
  ];

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      {cards.map((card, index) => {
        const change = calculateChange(card.value, card.previousValue);
        const isPositive = change !== null && change > 0;
        const isNegative = change !== null && change < 0;
        
        return (
          <Col key={index} xs={24} sm={12} lg={6}>
            <Card 
              size="small"
              style={{
                borderLeft: `4px solid ${card.color}`,
                height: '100%'
              }}
            >
              <Statistic
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {card.icon}
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      {card.title}
                    </Text>
                  </div>
                }
                value={card.value}
                formatter={card.formatter}
                valueStyle={{
                  color: card.color,
                  fontSize: '20px',
                  fontWeight: 'bold'
                }}
              />
              
              {/* Değişim yüzdesi göstergesi */}
              {change !== null && (
                <div style={{ 
                  marginTop: 8, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 4 
                }}>
                  {isPositive && <ArrowUpOutlined style={{ color: '#52c41a', fontSize: 12 }} />}
                  {isNegative && <ArrowDownOutlined style={{ color: '#f5222d', fontSize: 12 }} />}
                  <Text 
                    style={{ 
                      fontSize: 12, 
                      color: isPositive ? '#52c41a' : isNegative ? '#f5222d' : '#666',
                      fontWeight: 500
                    }}
                  >
                    {isPositive ? '+' : ''}{change.toFixed(1)}%
                  </Text>
                  <Text 
                    type="secondary" 
                    style={{ fontSize: 11 }}
                  >
                    (önceki döneme göre)
                  </Text>
                </div>
              )}
              
              {/* Veri yoksa göster */}
              {change === null && previousData && previousData.length === 0 && (
                <div style={{ marginTop: 8 }}>
                  <Text 
                    type="secondary" 
                    style={{ fontSize: 11 }}
                  >
                    (karşılaştırma verisi yok)
                  </Text>
                </div>
              )}
            </Card>
          </Col>
        );
      })}
    </Row>
  );
};

export default SummaryCards;